import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Shared Picture Manager upload dialog.
 *
 * Flow:
 *  1. User fills metadata (category, tags, caption) — fields are shown FIRST
 *  2. User drops / selects files  →  they are staged (not yet uploaded)
 *  3. User clicks "Upload N image(s)"  →  upload fires with the current metadata
 *
 * Props:
 *  open           – bool
 *  onOpenChange   – (bool) => void
 *  folderId       – string | null  (default null → uncategorized)
 *  onImageAdded   – ({ id, url, caption }) => void  — called once per successful upload
 */
export function PictureManagerUploadDialog({ open, onOpenChange, folderId = null, onImageAdded }) {
  const [categories, setCategories] = useState([]);

  // Metadata fields
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadTagInput, setUploadTagInput] = useState('');
  const [uploadTagList, setUploadTagList] = useState([]);
  const [uploadTagSuggestions, setUploadTagSuggestions] = useState([]);
  const [uploadCaption, setUploadCaption] = useState('');

  // Staged files (queued but not yet uploaded)
  const [stagedFiles, setStagedFiles] = useState([]);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);

  const fileInputRef = useRef(null);

  // Fetch categories whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/picture-manager/categories`)
      .then(r => setCategories(r.data || []))
      .catch(() => {});
  }, [open]);

  // --- Tag helpers ---
  const fetchTagSuggestions = async (query) => {
    if (!query.trim()) { setUploadTagSuggestions([]); return; }
    try {
      const res = await axios.get(`${API}/picture-manager/tags/suggestions`, { params: { query } });
      setUploadTagSuggestions(res.data.filter(s => !uploadTagList.includes(s.name)));
    } catch { /* silent */ }
  };

  const addTag = (name) => {
    const normalized = name.trim().toLowerCase();
    if (normalized && !uploadTagList.includes(normalized)) {
      setUploadTagList(prev => [...prev, normalized]);
    }
    setUploadTagInput('');
    setUploadTagSuggestions([]);
  };

  const removeTag = (name) => setUploadTagList(prev => prev.filter(t => t !== name));

  // --- File staging ---
  const stageFiles = (files) => {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (newFiles.length === 0) { toast.error('Please select image files only'); return; }
    setStagedFiles(prev => [...prev, ...newFiles]);
    // reset input so the same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeStagedFile = (index) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // --- Upload (triggered by button) ---
  const handleUpload = async () => {
    if (stagedFiles.length === 0 || uploading) return;

    // Capture everything before clearing state
    const filesToUpload = [...stagedFiles];
    const tagsString = uploadTagList.join(', ');
    const captionSnapshot = uploadCaption.trim();
    const categorySnapshot = uploadCategory;

    setStagedFiles([]);
    setUploading(true);
    const progressList = filesToUpload.map(f => ({ name: f.name, status: 'pending', progress: 0 }));
    setUploadProgress(progressList);

    for (let i = 0; i < filesToUpload.length; i++) {
      progressList[i].status = 'uploading';
      setUploadProgress([...progressList]);

      try {
        const formData = new FormData();
        formData.append('file', filesToUpload[i]);

        if (folderId) formData.append('folder_id', folderId);
        if (categorySnapshot && categorySnapshot !== 'none') formData.append('category_id', categorySnapshot);
        if (tagsString.trim()) formData.append('tags', tagsString.trim());
        if (captionSnapshot) formData.append('caption', captionSnapshot);

        const response = await axios.post(`${API}/picture-manager/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded * 100) / e.total);
            progressList[i].progress = pct;
            setUploadProgress([...progressList]);
          }
        });

        progressList[i].status = 'done';
        progressList[i].progress = 100;
        setUploadProgress([...progressList]);

        if (onImageAdded) {
          const img = response.data.image;
          onImageAdded({ id: img.id, url: img.url, caption: img.caption || '' });
        }
      } catch (error) {
        progressList[i].status = 'error';
        progressList[i].error = error.response?.data?.detail || 'Upload failed';
        setUploadProgress([...progressList]);
      }
    }

    setUploading(false);
    const done = progressList.filter(p => p.status === 'done').length;
    const failed = progressList.filter(p => p.status === 'error').length;
    if (done > 0) toast.success(`Uploaded ${done} image${done > 1 ? 's' : ''}`);
    if (failed > 0) toast.error(`${failed} upload${failed > 1 ? 's' : ''} failed`);
  };

  const resetAndClose = () => {
    setStagedFiles([]);
    setUploadProgress([]);
    setUploadCategory('');
    setUploadTagInput('');
    setUploadTagList([]);
    setUploadTagSuggestions([]);
    setUploadCaption('');
    onOpenChange(false);
  };

  const allDone = uploadProgress.length > 0 && !uploading;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetAndClose(); else onOpenChange(true); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Images</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Best Practices reminder */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
            <p className="font-medium text-amber-900 mb-1">Before you upload</p>
            <ul className="text-amber-800 space-y-0.5 text-xs">
              <li>• <strong>Compress</strong> images first —{' '}
                <a href="https://tinyjpg.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">TinyJPG</a>,{' '}
                <a href="https://www.iloveimg.com/compress-image" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">iLoveIMG</a>,{' '}
                <a href="https://compressor.io" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">Compressor.io</a>
              </li>
              <li>• <strong>Watermark</strong> original creations —{' '}
                <a href="https://watermarkly.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">Watermarkly</a>,{' '}
                <a href="https://watermarkimage.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">WatermarkImage.com</a>
              </li>
            </ul>
          </div>

          {/* ── Metadata fields FIRST so the user fills them before dropping files ── */}

          {/* Category */}
          <div>
            <Label className="text-sm">Category (optional)</Label>
            <Select value={uploadCategory} onValueChange={setUploadCategory}>
              <SelectTrigger className="mt-1" data-testid="pm-upload-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tags — chip input with autocomplete */}
          <div>
            <Label className="text-sm">Tags (optional)</Label>
            <div className="mt-1 flex flex-wrap gap-1 mb-1">
              {uploadTagList.map(tag => (
                <Badge key={tag} variant="secondary" className="flex items-center gap-1 text-xs">
                  {tag}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(tag)} />
                </Badge>
              ))}
            </div>
            <div className="relative">
              <Input
                placeholder="Type a tag and press Enter..."
                value={uploadTagInput}
                onChange={(e) => { setUploadTagInput(e.target.value); fetchTagSuggestions(e.target.value); }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && uploadTagInput.trim()) {
                    e.preventDefault();
                    addTag(uploadTagInput);
                  }
                }}
                className="text-sm"
                data-testid="pm-upload-tag-input"
              />
              {uploadTagSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 max-h-32 overflow-y-auto">
                  {uploadTagSuggestions.map(s => (
                    <div
                      key={s.name}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm"
                      onMouseDown={(e) => { e.preventDefault(); addTag(s.name); }}
                    >
                      {s.color && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />}
                      <span>{s.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">Press Enter or comma to add a tag</p>
          </div>

          {/* Caption */}
          <div>
            <Label className="text-sm">Caption (optional)</Label>
            <Input
              placeholder="Describe what's in this image..."
              value={uploadCaption}
              onChange={(e) => setUploadCaption(e.target.value)}
              className="mt-1"
              data-testid="pm-upload-caption-input"
            />
            <p className="text-xs text-gray-400 mt-1">Pre-fills everywhere this image is used</p>
          </div>

          {/* ── Drop zone — comes AFTER metadata so user fills details first ── */}
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#14B8A6] transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDrop={(e) => { e.preventDefault(); stageFiles(e.dataTransfer.files); }}
            onDragOver={(e) => e.preventDefault()}
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              Drag and drop images here, or <span className="text-[#14B8A6]">browse</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WebP, BMP — Max 10MB</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => stageFiles(e.target.files)}
              data-testid="pm-upload-file-input"
            />
          </div>

          {/* Staged files list */}
          {stagedFiles.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500">{stagedFiles.length} file{stagedFiles.length > 1 ? 's' : ''} queued</p>
              {stagedFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-2 py-1">
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-gray-400 text-xs">{(f.size / 1024).toFixed(0)} KB</span>
                  <X className="w-3.5 h-3.5 text-gray-400 cursor-pointer hover:text-red-500" onClick={() => removeStagedFile(i)} />
                </div>
              ))}
            </div>
          )}

          {/* Upload progress */}
          {uploadProgress.length > 0 && (
            <div className="space-y-1.5">
              {uploadProgress.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {item.status === 'pending'   && <div className="w-4 h-4 rounded-full bg-gray-200 shrink-0" />}
                  {item.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin text-[#14B8A6] shrink-0" />}
                  {item.status === 'done'      && <Check className="w-4 h-4 text-green-500 shrink-0" />}
                  {item.status === 'error'     && <X className="w-4 h-4 text-red-500 shrink-0" />}
                  <span className="flex-1 truncate">{item.name}</span>
                  {item.status === 'uploading' && <span className="text-gray-400 text-xs">{item.progress}%</span>}
                  {item.status === 'error'     && <span className="text-red-500 text-xs truncate">{item.error}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          {/* Upload button — visible only when files are staged and not yet uploading */}
          {stagedFiles.length > 0 && !uploading && (
            <Button
              className="bg-[#14B8A6] hover:bg-[#0D9488] text-white"
              onClick={handleUpload}
              data-testid="pm-upload-submit-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload {stagedFiles.length} image{stagedFiles.length > 1 ? 's' : ''}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={resetAndClose}
            disabled={uploading}
            data-testid="pm-upload-done-btn"
          >
            {allDone ? 'Done' : uploading ? 'Uploading...' : 'Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PictureManagerUploadDialog;
